/**
 * guideAnchorRegistry — B-2026-0088 Phase A1
 *
 * Unified registry mapping guide keys to their corresponding on-screen DOM
 * elements. Used by the anchor highlight system (Phase A3) to point an
 * arrow at the element being described.
 *
 * Resolution strategy (in `resolveAnchorElement`):
 *   1. If `elementId` is set, try `document.getElementById(elementId)`.
 *   2. If `dataAttr` is set, try
 *      `document.querySelector('[data-attr-name="value"]')`.
 *   3. If neither matches, returns null (fallbackToCard controls overlay
 *      behaviour — true = show nothing, false = show card-edge arrow).
 *
 * All 36+ guide keys (eval + studio) are represented here. This replaces
 * the separate `SECTION_TO_KEY` / `FIELD_ID_TO_GUIDE_KEY` lookup tables
 * for anchor resolution purposes (those maps remain unchanged for content
 * resolution).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GuideSurface = 'evaluation-results' | 'studio';

export interface AnchorDescriptor {
  /** Which surface this anchor lives on. */
  surface: GuideSurface;
  /**
   * A data-attribute selector to find the element.
   * `name` is the attribute name; `value` is the expected attribute value.
   */
  dataAttr?: {
    name: 'data-guide-section' | 'data-field-id' | 'data-guide-anchor';
    value: string;
  };
  /** Direct element ID as an alternative to `dataAttr`. */
  elementId?: string;
  /**
   * When true and no element is found, the highlight is suppressed entirely.
   * When false and no element is found, the arrow points to the card edge.
   */
  fallbackToCard: boolean;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/**
 * Map from guide key → AnchorDescriptor.
 *
 * Eval keys use `data-guide-section` values that already exist in
 * EvaluationResults.tsx (guarded by the hard rule: do NOT modify that file).
 *
 * Studio keys use `data-field-id` values from FIELD_IDS, or `data-guide-anchor`
 * values added to RegionAnnotator panel containers in Phase A2.
 */
export const GUIDE_ANCHOR_REGISTRY: Record<string, AnchorDescriptor> = {
  // ── Evaluation Results ───────────────────────────────────────────────────

  'overall-score': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'hero-overall' },
    fallbackToCard: false,
  },
  'body-form-score': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'body-form-card' },
    fallbackToCard: false,
  },
  'goals-score': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'goal-achievements' },
    fallbackToCard: false,
  },
  'eom-score': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'eom-card' },
    fallbackToCard: false,
  },
  'phase-selected': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'phase-breakdown' },
    fallbackToCard: false,
  },
  'region-selected': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'body-form-card' },
    fallbackToCard: true,
  },
  'view-mode-2d': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'video-analysis' },
    fallbackToCard: false,
  },
  'view-mode-3d': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'video-analysis' },
    fallbackToCard: false,
  },
  'coaching-narrative': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'coaching-narrative' },
    fallbackToCard: false,
  },
  'video-analysis': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'video-analysis' },
    fallbackToCard: false,
  },
  'goal-achievements': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'goal-achievements' },
    fallbackToCard: false,
  },
  'gemini-visual': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'gemini-visual' },
    fallbackToCard: false,
  },
  'share-card': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'share-card' },
    fallbackToCard: false,
  },
  'comments': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'comments' },
    fallbackToCard: false,
  },
  'auto-fail': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'hero-overall' },
    fallbackToCard: true,
  },
  'mirroring-badge': {
    surface: 'evaluation-results',
    dataAttr: { name: 'data-guide-section', value: 'hero-overall' },
    fallbackToCard: true,
  },

  // ── Studio — field-level ──────────────────────────────────────────────────

  'field-body-region-importance': {
    surface: 'studio',
    dataAttr: { name: 'data-field-id', value: 'body-region-importance' },
    fallbackToCard: false,
  },
  'field-goal-landmark-picker': {
    surface: 'studio',
    dataAttr: { name: 'data-field-id', value: 'goal-landmark-picker' },
    fallbackToCard: false,
  },
  'field-phase-selector': {
    surface: 'studio',
    dataAttr: { name: 'data-field-id', value: 'phase-selector' },
    fallbackToCard: false,
  },
  'field-annotation-description': {
    surface: 'studio',
    dataAttr: { name: 'data-field-id', value: 'annotation-description' },
    fallbackToCard: false,
  },
  'field-scoring-studio-panel': {
    surface: 'studio',
    dataAttr: { name: 'data-field-id', value: 'scoring-studio-panel' },
    fallbackToCard: false,
  },

  // ── Studio — tool/panel level ─────────────────────────────────────────────

  'tool-body-region': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-body-region' },
    fallbackToCard: false,
  },
  'tool-distance': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-custom-goals' },
    fallbackToCard: false,
  },
  'tool-movement': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-custom-goals' },
    fallbackToCard: false,
  },
  'tool-axis': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-custom-goals' },
    fallbackToCard: false,
  },
  'tool-timing': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-custom-goals' },
    fallbackToCard: false,
  },
  'tool-joint-angle': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-custom-goals' },
    fallbackToCard: false,
  },
  'tool-scoring-emphasis': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-scoring' },
    fallbackToCard: false,
  },

  // ── Studio — deep settings ────────────────────────────────────────────────

  'deep-settings-body-form': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-scoring' },
    fallbackToCard: false,
  },
  'deep-settings-eom': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-scoring' },
    fallbackToCard: false,
  },
  'deep-settings-scoring-emphasis': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-scoring' },
    fallbackToCard: false,
  },

  // ── Studio — phase/timeline ───────────────────────────────────────────────

  'phase-timeline': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-phase-timeline' },
    fallbackToCard: false,
  },
  // Note: 'phase-selected' is defined above for eval-results surface.
  // On studio, Phase A3 resolves via the phase-timeline anchor instead.
  // The studio overlay will rely on the eval-results entry (both point to a
  // phase-related section) or fall back to card edge when anchor not found.

  // ── Studio — overview / fallback ─────────────────────────────────────────

  'studio-overview': {
    surface: 'studio',
    dataAttr: { name: 'data-guide-anchor', value: 'studio-panel-body-region' },
    fallbackToCard: true,
  },

  // ── Shared default ────────────────────────────────────────────────────────

  default: {
    surface: 'evaluation-results',
    fallbackToCard: true,
  },
};

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolve the DOM element for a guide key.
 *
 * Tries `elementId` first, then `dataAttr` selector.
 * Returns null if no element matches.
 */
export function resolveAnchorElement(key: string): Element | null {
  const descriptor = GUIDE_ANCHOR_REGISTRY[key];
  if (!descriptor) return null;

  if (descriptor.elementId) {
    const el = document.getElementById(descriptor.elementId);
    if (el) return el;
  }

  if (descriptor.dataAttr) {
    const { name, value } = descriptor.dataAttr;
    const el = document.querySelector(`[${name}="${value}"]`);
    if (el) return el;
  }

  return null;
}
